import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { cloneDeep } from 'lodash';
import { useFeatureFlags } from '~/helpers/FeatureFlagsContext';
import {
  generateDocs,
  getAssetManagerDocs,
  getIsAssetManagersChanged,
  updateForm,
} from '~/store/identification-form/thunks/thunks.ts';
import {
  IdentificationFormStepNames,
  IdentificationFormSteps,
  TInfoModal,
} from '~/modules/identification/types.ts';
import { Box, Button, Divider } from '@mui/material';
import { useManagersAndRepresentativesForm } from './hooks/useManagersAndRepresentativesForm';
import ClientRepresentatives from './components/ClientRepresentatives';
import { AssetManagerList } from './components/AssetManagerList';
import { FormCheckbox } from '~/components/FormComponents/FormCheckbox';
import { FormProvider, useWatch } from 'react-hook-form';
import {
  updateAssetManagerVerificationStatus,
  updateFormData,
  updateFormDataBack,
} from '~/store/identification-form/slices';
import FullPageLoader from '~/components/FullPageLoader/FullPageLoader';
import {
  ManagerVerificationStatus,
  TAssetManagerStatusResponse,
  TManagersAndRepresentatives,
} from './types';
import { styles } from '../../styles';
import { useAppDispatch } from '~/store/appDispatch';
import { TIdentificationForm } from '~/store/identification-form/types';
import { EMPTY_CLIENT_REPRESENTATIVE, testDataManagersAndRepresentatives } from './constants';
import { InformationMessageBlock } from '../../components/InformationMessage/InformationMessageBlock';
import { infoMessages } from '~/constants/infoMessages.ts';
import { checkVerificationStatus, updateStatuses } from './utils';
import { b2IntegrationAssetManagersPrefill } from '~/store/identification-form/thunks/assetManagersThunk';
import { commonStyles } from '~/theme/styles/commonStyles';
import {
  selectApplicationId,
  selectIsReidentification,
} from '~/store/identification-form/selectors';
import { onInvalidForm } from '~/helpers/utils';
import { useIdentificationFormContext } from '../../contexts/IdentificationFormContext';
import { SetDirtyModalValues } from '../../components/SetDirtyModalValues';
import { checkRegenerateDocs } from '~/store/identification-form/thunks/documentThunks';

interface IManagersAndRepresentativesProps {
  onNextStep: () => void;
}

export const ManagersAndRepresentatives = ({ onNextStep }: IManagersAndRepresentativesProps) => {
  const dispatch = useAppDispatch();
  const { isTestEnvironmentEnabled } = useFeatureFlags();

  const isReidentification = useSelector(selectIsReidentification);
  const applicationId = useSelector(selectApplicationId);

  const form = useManagersAndRepresentativesForm();
  const { isDirty, isSubmitting } = form.formState;
  const { setValue } = form;

  const { setInfoModal, defaultModalInfo } = useIdentificationFormContext();

  const [statusesLoading, setStatusesLoading] = useState(false);
  const handleFillTestData = () =>
    form.reset(testDataManagersAndRepresentatives, { keepDefaultValues: true });

  const assetManagers = useWatch({
    control: form.control,
    name: 'assetManagers',
  });

  const showUpdateButton = assetManagers?.some((item) => !!item.verificationStatus);

  const onSubmit =
    (isDirty: boolean, disableGoNext?: boolean) => async (values: TManagersAndRepresentatives) => {
      if (isDirty) {
        const formValues: TManagersAndRepresentatives = {
          ...values,
          isUnifiedAccountAccess: values.isUnifiedAccountAccess,
          assetManagers: checkVerificationStatus(values.assetManagers),
        };
        const data = {
          managersAndRepresentatives: formValues,
          applicationId: applicationId as number,
        };

        try {
          await dispatch(
            updateForm({ data, reportId: IdentificationFormSteps.managersAndRepresentatives })
          );
          const formData = {
            field:
              IdentificationFormStepNames.managersAndRepresentatives as keyof TIdentificationForm,
            data: cloneDeep(formValues),
          };
          dispatch(updateFormData(formData));
          dispatch(updateFormDataBack(formData));
          if (isReidentification) {
            await dispatch(
              b2IntegrationAssetManagersPrefill({ applicationId: applicationId as number })
            );
            dispatch(getIsAssetManagersChanged({ applicationId: applicationId as number }));
          }
          !disableGoNext && onNextStep();
        } catch (error) {
          console.error(error);
        }
      } else {
        !disableGoNext && onNextStep();
      }
    };

  const handleUpdateStatuses = async () => {
    try {
      const callbackFunc = (index: number, manager: TAssetManagerStatusResponse) => {
        setValue(
          `assetManagers.${index}.verificationStatus`,
          manager.status || ManagerVerificationStatus.IN_PROGRESS
        );
        dispatch(updateAssetManagerVerificationStatus({ index, status: manager.status }));
        setValue(`assetManagers.${index}.id`, manager.assetManagerId || null);
      };

      setStatusesLoading(true);
      await updateStatuses({
        applicationId,
        callbackFunc,
        assetManagers: form.getValues().assetManagers,
      });
      await dispatch(getAssetManagerDocs({ applicationId: applicationId as number }));
      const data = {
        managersAndRepresentatives: cloneDeep(form.getValues()),
        applicationId: applicationId as number,
      };
      const docs = await dispatch(
        generateDocs({
          reportId: IdentificationFormSteps.managersAndRepresentatives,
          data,
        })
      ).unwrap();
      const { applicationId: appId, ...submittedForm } = data;
      await dispatch(
        checkRegenerateDocs({
          docs,
          appId: appId as number,
          reportId: IdentificationFormSteps.managersAndRepresentatives,
          data: submittedForm as Partial<TIdentificationForm>,
        })
      );
      setStatusesLoading(false);
    } catch (error) {
      console.error(error);
    }
  };

  const handleClientRepresentativeCheckbox = (isChecked: boolean) => {
    setValue('clientRepresentatives', isChecked ? [] : [EMPTY_CLIENT_REPRESENTATIVE]);
  };

  const handleOpenModal = useCallback((onConfirm: () => void) => {
    setInfoModal((prev: TInfoModal) => ({
      ...prev,
      isOpened: true,
      message:
        'У разі видалення та повторного додавання, процедуру верифікації потрібно буде пройти знову. Це може зайняти додатковий час. Продовжити видалення?',
      onClose: () => setInfoModal(defaultModalInfo),
      onPrimaryButtonClick: () => setInfoModal(defaultModalInfo),
      title: 'Верифікацію вже пройдено',
      primaryButtonTitle: 'Скасувати',
      secondaryButtonTitle: 'Видалити',
      onSecondaryButtonClick: () => {
        onConfirm();
        setInfoModal(defaultModalInfo);
      },
    }));
  }, [defaultModalInfo, setInfoModal]);

  return (
    <FormProvider {...form}>
      <Box
        component="form"
        onSubmit={form.handleSubmit(onSubmit(isDirty), onInvalidForm)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
          }
        }}
      >
        <Box sx={{ ...commonStyles.columnFlexContainer, gap: 3 }}>
          {isReidentification && (
            <InformationMessageBlock informationText={infoMessages.RE_VERIFICATION_MESSAGE} />
          )}
          <Box sx={styles.managersContainer}>
            {isTestEnvironmentEnabled && (
              <Button variant={'outlined'} onClick={handleFillTestData}>
                Заповнити даними
              </Button>
            )}
            {showUpdateButton && (
              <Button variant="contained" onClick={handleUpdateStatuses} loading={statusesLoading}>
                Оновлення статусу
              </Button>
            )}
          </Box>
          <AssetManagerList
            handleOpenDeleteModal={handleOpenModal}
          />
          <Box sx={{ width: '100%', mb: 1 }}>
            <Divider sx={styles.divider} />
          </Box>
          <Box sx={{ width: '100%' }}>
            <FormCheckbox
              label="Представники клієнта відсутні"
              name="isClientRepresentativesAbsent"
              onChange={handleClientRepresentativeCheckbox}
            />
          </Box>
          <ClientRepresentatives />
          <Button
            disabled={isSubmitting}
            type="submit"
            variant="contained"
            sx={{ alignSelf: 'center' }}
          >
            Продовжити
          </Button>
        </Box>
        <SetDirtyModalValues
          saveValues={form.handleSubmit(onSubmit(isDirty, true), onInvalidForm)}
        />
        {isSubmitting && <FullPageLoader />}
      </Box>
    </FormProvider>
  );
};
